import { ImageUriUpload } from '@spike/model';
import clsx from 'clsx';
import { Label } from 'components/UI/V3/Forms/Label/Label';
import isEmpty from 'lodash/isEmpty';
import { ChangeEvent, FunctionComponent, useMemo, useRef, useState } from 'react';
import AddVideo from './AddVideo';
import Video from './Video';
import styles from './VideosSelector.module.scss';
import VideosSpecifications from './VideosSpecifications';

export interface VideoWrapper {
    id?: number;
    url: string;
    fileToUpload?: File | ImageUriUpload | null;
    destroy?: boolean;
}

export type VideosSelectorSize = 'small' | 'regular';

interface VideosSelectorProps {
    title?: string;
    maxVideos?: number;
    videos: Array<VideoWrapper>;
    showCoverTag?: boolean;
    multipleSelection?: boolean;
    size?: VideosSelectorSize;
    className?: string;
    onChange?: (videos: Array<VideoWrapper>) => void;
}

const validateFile = (file: File) => {
    const fileSizeMb = file.size / (1000 * 1000);
    if (fileSizeMb > 50) {
        return `Video ${file.name} size exceeds the 50MB limit.`;
    }

    if (!file.type.startsWith('video/')) {
        return `Video ${file.name} has an unsupported file format. Please upload a valid video file.`;
    }
};

const isFileValid = (file: File) => {
    return validateFile(file) === undefined;
};

const findFile = (files: Array<File>, file: File) => {
    return files.find(
        f =>
            f.name === file.name && f.size === file.size && f.type === file.type && f.lastModified === file.lastModified
    );
};

const convertToVideoWrapper = (file: File) => {
    return {
        url: URL.createObjectURL(file),
        fileToUpload: file
    };
};

const VideosSelector: FunctionComponent<VideosSelectorProps> = props => {
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const maxVideos = props.maxVideos ?? 3;

    const [error, setError] = useState<string | Array<string> | null>(null);

    const validFiles = useMemo(() => {
        return props.videos.filter(video => !video.destroy);
    }, [props.videos]);

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const filesToUpload = event.target.files;

        if (!filesToUpload || filesToUpload.length === 0) {
            return;
        }

        const isMultipleSelection = props.multipleSelection ?? false;

        if (isMultipleSelection && filesToUpload.length > maxVideos) {
            setError(`You can only upload up to ${maxVideos} videos.`);
            return;
        }

        const files = Array.from(filesToUpload);

        const filesWithErrors = files.map(file => {
            return { ...convertToVideoWrapper(file), error: validateFile(file) };
        });

        const existingFiles = props.videos
            .filter(video => video.fileToUpload)
            .map(video => video.fileToUpload) as Array<File>;

        const validFiles = filesWithErrors
            .filter(file => file.error === undefined)
            .map(item => convertToVideoWrapper(item.fileToUpload));

        const errors = filesWithErrors
            .filter(file => file.error !== undefined)
            .map(file => file.error) as Array<string>;

        if (!isMultipleSelection && errors.length > 0) {
            // Early return for single file selection
            setError(errors[0]);
            return;
        }

        let newVideos = [...props.videos, ...validFiles.filter(file => !findFile(existingFiles, file.fileToUpload))];

        if (newVideos.length > maxVideos) {
            newVideos = newVideos.slice(0, maxVideos);
            errors.unshift(`You can only upload up to ${maxVideos} videos.`);
        }

        if (isMultipleSelection && errors.length > 0) {
            props.onChange?.(newVideos);
            setError(errors);
            return;
        }

        setError(null);

        props.onChange?.(newVideos);
    };

    const handleAddVideo = () => {
        fileInputRef.current?.click();
    };

    const handleRemoveVideo = (url: string) => {
        props.onChange?.(
            props.videos
                .map(video => (video.url === url ? (!video.fileToUpload ? { ...video, destroy: true } : null) : video))
                .filter(video => video !== null)
        );
    };

    return (
        <div className={clsx(styles.container, props.className, props.size && styles[props.size])}>
            {!isEmpty(props.title) && <Label className={styles.title}>{props.title}</Label>}

            <div className={clsx(styles.videosContainer)}>
                {props.videos
                    .filter(video => !(video.destroy ?? false))
                    .map((video, index) => (
                        <Video
                            key={index}
                            isCover={props.showCoverTag && index === 0}
                            src={video.url}
                            onRemove={() => handleRemoveVideo(video.url)}
                        />
                    ))}

                {validFiles.length < maxVideos && <AddVideo onClick={handleAddVideo} />}
            </div>

            <VideosSpecifications
                maxVideos={maxVideos}
                className={styles.specifications}
            />

            {error !== null &&
                (typeof error === 'string' ? (
                    <span className={styles.fileErrorMessage}>{error}</span>
                ) : (
                    error.map((error, index) => (
                        <span
                            key={index}
                            className={styles.fileErrorMessage}
                        >
                            {error}
                        </span>
                    ))
                ))}

            <input
                type="file"
                accept="video/*"
                multiple={props.multipleSelection}
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileChange}
            />
        </div>
    );
};

export default VideosSelector;
